import React, { useState } from 'react';
import { Product, ProductFormData } from '../types';

interface ProductModalProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: ProductFormData) => Promise<void>;
}

const ProductModal: React.FC<ProductModalProps> = ({ product, isOpen, onClose, onConfirm }) => {
  const [formData, setFormData] = useState<ProductFormData>({
    nome_responsavel: '',
    telefone_responsavel: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Função para aplicar máscara de telefone
  const applyPhoneMask = (value: string): string => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');

    // Aplica máscara conforme o tamanho
    if (numbers.length <= 10) {
      // Telefone fixo: (00) 0000-0000
      return numbers
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    } else {
      // Celular: (00) 00000-0000
      return numbers
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d)/, '$1-$2')
        .replace(/(-\d{4})\d+?$/, '$1');
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const maskedValue = applyPhoneMask(e.target.value);
    setFormData({ ...formData, telefone_responsavel: maskedValue });
  };

  if (!isOpen || !product) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.nome_responsavel.trim() || !formData.telefone_responsavel.trim()) {
      setError('Por favor, preencha todos os campos');
      return;
    }

    setIsLoading(true);
    try {
      await onConfirm(formData);
      setFormData({ nome_responsavel: '', telefone_responsavel: '' });
      setError(null);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Erro ao selecionar produto');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-gradient-card rounded-2xl shadow-soft-lg max-w-md w-full p-8 border border-white/50 transform transition-all animate-scale-in">
        <div className="mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center mb-4 mx-auto">
            <span className="text-3xl">🎁</span>
          </div>
          <h2 className="text-2xl font-bold text-text-primary text-center mb-2">
            Selecionar Produto
          </h2>
          <p className="text-lg font-semibold text-primary-700 text-center">
            {product.nome}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-text-primary font-semibold mb-2 text-sm">
              Seu Nome *
            </label>
            <input
              type="text"
              value={formData.nome_responsavel}
              onChange={(e) => setFormData({ ...formData, nome_responsavel: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white/50 text-text-primary placeholder-text-light"
              placeholder="Digite seu nome completo"
              disabled={isLoading}
            />
          </div>

          <div>
            <label className="block text-text-primary font-semibold mb-2 text-sm">
              Seu Telefone *
            </label>
            <input
              type="tel"
              value={formData.telefone_responsavel}
              onChange={handlePhoneChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white/50 text-text-primary placeholder-text-light"
              placeholder="(00) 00000-0000"
              maxLength={15}
              disabled={isLoading}
            />
            <p className="text-xs text-text-light mt-1">
              Formato: (00) 00000-0000 ou (00) 0000-0000
            </p>
          </div>

          {error && (
            <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-lg animate-shake">
              <p className="font-medium">{error}</p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-gray-200 text-text-secondary rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all font-semibold shadow-lg shadow-primary-300 hover:shadow-primary-400 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></span>
                  Salvando...
                </span>
              ) : (
                'Confirmar'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductModal;

